<script setup>
import { ref, computed, onBeforeUnmount } from 'vue';
import { useRouter } from 'vue-router';

import microsoftClient from 'dashboard/api/channel/microsoftClient';
import googleClient from 'dashboard/api/channel/googleClient';
import SettingsSubPageHeader from '../../../SettingsSubPageHeader.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

import { useAlert } from 'dashboard/composables';

const props = defineProps({
  provider: {
    type: String,
    required: true,
    validate: value => ['microsoft', 'google'].includes(value),
  },
  title: {
    type: String,
    required: true,
  },
  description: {
    type: String,
    required: true,
  },
  submitButtonText: {
    type: String,
    required: true,
  },
  errorMessage: {
    type: String,
    required: true,
  },
});

const OAUTH_MESSAGE_TYPE = 'inbox_oauth_done';
const router = useRouter();
const isRequestingAuthorization = ref(false);
let oauthPopup = null;
let oauthBroadcast = null;

function isOauthDonePayload(data) {
  return data?.type === OAUTH_MESSAGE_TYPE && data.inboxId;
}

function finishOauth(payload) {
  window.removeEventListener('message', onOauthMessage);
  window.removeEventListener('storage', onOauthStorage);
  if (oauthBroadcast) {
    oauthBroadcast.close();
    oauthBroadcast = null;
  }
  if (oauthPopup && !oauthPopup.closed) oauthPopup.close();
  oauthPopup = null;
  isRequestingAuthorization.value = false;

  router.push({
    name: payload.alreadyExists
      ? 'settings_inbox_show'
      : 'settings_inboxes_add_agents',
    params: payload.alreadyExists
      ? { inboxId: payload.inboxId }
      : { inbox_id: payload.inboxId },
  });
}

function onOauthMessage(event) {
  if (event.origin !== window.location.origin) return;
  if (!isOauthDonePayload(event.data)) return;
  finishOauth(event.data);
}

function onOauthStorage(event) {
  if (!event.key?.startsWith('inbox_oauth_done:')) return;
  try {
    const payload = JSON.parse(event.newValue);
    if (isOauthDonePayload(payload)) finishOauth(payload);
  } catch {
    // Ignore malformed storage events.
  }
}

function startOauthWatchers() {
  window.addEventListener('message', onOauthMessage);
  window.addEventListener('storage', onOauthStorage);
  if (typeof BroadcastChannel !== 'undefined') {
    oauthBroadcast = new BroadcastChannel('inbox_oauth');
    oauthBroadcast.onmessage = event => {
      if (!isOauthDonePayload(event.data)) return;
      finishOauth(event.data);
    };
  }
}

onBeforeUnmount(() => {
  window.removeEventListener('message', onOauthMessage);
  window.removeEventListener('storage', onOauthStorage);
  if (oauthBroadcast) oauthBroadcast.close();
});

const client = computed(() => {
  if (props.provider === 'microsoft') {
    return microsoftClient;
  }

  return googleClient;
});

async function requestAuthorization() {
  try {
    isRequestingAuthorization.value = true;
    const response = await client.value.generateAuthorization({ popup: true });
    const {
      data: { url },
    } = response;

    const width = 600;
    const height = 720;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;
    oauthPopup = window.open(
      url,
      'inbox_oauth',
      `popup=yes,width=${width},height=${height},left=${left},top=${top}`
    );
    if (!oauthPopup) {
      useAlert(props.errorMessage);
      return;
    }
    startOauthWatchers();
  } catch (error) {
    useAlert(props.errorMessage);
  } finally {
    isRequestingAuthorization.value = false;
  }
}
</script>

<template>
  <div class="h-full w-full p-6 col-span-6">
    <SettingsSubPageHeader
      :header-title="title"
      :header-content="description"
    />
    <form class="mt-6" @submit.prevent="requestAuthorization">
      <NextButton
        :is-loading="isRequestingAuthorization"
        type="submit"
        solid
        blue
        :label="submitButtonText"
      />
    </form>
  </div>
</template>
